import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Form, Formik, useFormikContext } from 'formik'
import * as yup from 'yup'
import {
  Autocomplete,
  Box,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useCodeList } from 'hooks/useCodeList'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import Input from 'components/Input/Input'
import theme from 'lib/theme'
import { CheckboxLabel } from 'components/CheckboxLabel/CheckboxLabel'
import { useAlert } from 'providers/AlertProvider'
import { usePutMasterProduct } from 'hooks/useMasterProducts'
import { useProductInfo } from './Product'

type FormikFieldType = {
  displayUnit: string | null
  netContent: string
  netContentUnit: { label: string; value: string }
  pricedBy: string | null
  pickedBy: string | null
  variableTradeItemTypeCode: string | null
  incrementValue: string
  initialValue: string
  salesConditions: string[]
}

const AutocompleteWithCodeList = ({
  fieldName,
  codes,
}: {
  fieldName: string
  codes: string[]
}) => {
  const { t } = useTranslation()
  const { setFieldValue, values } = useFormikContext()

  return (
    <Autocomplete
      disablePortal
      noOptionsText={
        codes.length === 0 ? t('noOptions') : t('noOptionsAutoComplete')
      }
      renderOption={(props, option) => {
        return (
          <li {...props} key={option}>
            {option}
          </li>
        )
      }}
      options={codes}
      onChange={(e, value, reason, details) => {
        if (reason === 'selectOption') {
          if (details) {
            setFieldValue(fieldName, details.option)
          }
        }
        if (reason === 'clear') {
          setFieldValue(fieldName, null)
        }
      }}
      value={
        (values as FormikFieldType)[
          fieldName as keyof FormikFieldType
        ] as string
      }
      isOptionEqualToValue={(option, value) => option === value}
      getOptionLabel={(option) => option ?? ''}
      renderInput={(params) => (
        <TextField
          {...params}
          name={fieldName}
          label={t(fieldName)}
          InputLabelProps={{
            shrink: true,
            sx: { top: '-5px', left: '-4px' },
          }}
          variant='filled'
          sx={{
            '& .MuiFilledInput-root': {
              paddingTop: theme.spacing(0.5),
            },
          }}
        />
      )}
      id={fieldName}
    />
  )
}

export const ProductSalesInfo = () => {
  const { t } = useTranslation()
  const {
    product,
    isLoading: isLoadingProduct,
    masterProduct,
  } = useProductInfo()
  const { setAlert } = useAlert()
  const { masterProductId } = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const { mutateAsync, isLoading: isLoadingPutRequest } = usePutMasterProduct(
    masterProductId as string
  )
  const {
    codeListTypes: displayUnits,
    error,
    isLoading: isLoadingMeasureCodes,
  } = useCodeList('x_sellingUnitOfMeasureCode')
  const {
    codeListTypes: pricedByMeasureTypeCodes,
    error: pricedByMeasureTypeCodeError,
    isLoading: isLoadingPriceByMeasureCodes,
  } = useCodeList('priceByMeasureTypeCode')
  const {
    codeListTypes: sellingByMeasureTypeCodes,
    error: sellingByMeasureTypeCodeError,
    isLoading: isLoadingByMeasureCodes,
  } = useCodeList('x_sellingByMeasureTypeCode')
  const {
    codeListTypes: variableTradeItemTypeCodes,
    error: variableTradeItemTypeCodeError,
    isLoading: isLoadingTradeItemCodes,
  } = useCodeList('variableTradeItemTypeCode')
  const {
    codeListTypes: measurementUnitCodes,
    error: measurementUnitCodeError,
    isLoading: isLoadingMeasurementUnitCodes,
  } = useCodeList('measurementUnitCode', true)
  const {
    codeListTypes: salesConditionCodes,
    error: salesConditionCodeError,
    isLoading: isLoadingSalesCodes,
  } = useCodeList('consumerSalesConditionCode', true)

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const initialValues = {
    displayUnit: product?.displayUnit || null,
    netContent: product?.netContent ? String(product.netContent) : '',
    netContentUnit:
      product?.netContentUnit && measurementUnitCodes?.length > 0
        ? measurementUnitCodes.find(
            (code: { value: string; label: string }) =>
              code.value === product.netContentUnit
          )
        : null,
    pricedBy: product?.pricedBy || null,
    pickedBy: product?.pickedBy || null,
    variableTradeItemTypeCode: product?.variableTradeItemTypeCode || null,
    initialValue: product?.initialValue ? String(product.initialValue) : '',
    incrementValue: product?.incrementValue
      ? String(product.incrementValue)
      : '',
    salesConditions: product?.salesConditions,
  }

  useEffect(() => {
    setIsLoading(
      isLoadingByMeasureCodes ||
        isLoadingMeasureCodes ||
        isLoadingMeasurementUnitCodes ||
        isLoadingPriceByMeasureCodes ||
        isLoadingSalesCodes ||
        isLoadingTradeItemCodes
    )
  }, [
    isLoadingByMeasureCodes,
    isLoadingMeasureCodes,
    isLoadingMeasurementUnitCodes,
    isLoadingPriceByMeasureCodes,
    isLoadingSalesCodes,
    isLoadingTradeItemCodes,
  ])

  const validationSchema = yup.object({
    netContent: yup.number().when('netContentUnit', {
      is: (unit: { value: string; label: string }) => Boolean(unit?.value),
      then: yup.number().required(t('required')),
    }),
    netContentUnit: yup
      .object()
      .nullable()
      .shape({ label: yup.string(), value: yup.string() }),
    initialValue: yup.number(),
    incrementValue: yup.number(),
  })

  const handleCheckboxChange = (
    selectedSalesConditions: string[],
    setFieldValue: (field: string, value: string[]) => void,
    salesCondition: { label: string; value: string }
  ) => {
    const newSelectedSalesConditions = selectedSalesConditions.includes(
      salesCondition.value
    )
      ? selectedSalesConditions.filter(
          (condition) => condition !== salesCondition.value
        )
      : [...selectedSalesConditions, salesCondition.value]
    setFieldValue('salesConditions', newSelectedSalesConditions)
  }

  const handleSubmit = async (values: Partial<FormikFieldType>) => {
    const masterProductCopy = { ...masterProduct }
    const temp = masterProductCopy.tradeItem.tradeItemInformation.extensions
    const netContentObj =
      values.netContent || values.netContentUnit
        ? [
            {
              $: Number(values.netContent),
              '@measurementUnitCode': values.netContentUnit?.value
                ? values.netContentUnit?.value
                : null,
            },
          ]
        : []

    const editedSalesConditions =
      values.salesConditions && values.salesConditions?.length > 0
        ? values.salesConditions
        : []

    temp.salesInformationModule = {
      ...temp.salesInformationModule,
      salesInformation: {
        ...temp.salesInformationModule?.salesInformation,
        priceByMeasureTypeCode: values.pricedBy ? values.pricedBy : null,
        x_sellingByMeasureTypeCode: values.pickedBy ? values.pickedBy : null,
        x_sellingUnitOfMeasureCode: values.displayUnit
          ? values.displayUnit
          : null,
        x_sellingContentInitial: values.initialValue
          ? Number(values.initialValue)
          : null,
        x_sellingContentIncrement: values.incrementValue
          ? Number(values.incrementValue)
          : null,
        consumerSalesConditionCode: editedSalesConditions,
      },
    }

    temp.tradeItemMeasurementsModule = {
      ...temp.tradeItemMeasurementsModule,
      tradeItemMeasurements: {
        ...temp.tradeItemMeasurementsModule?.tradeItemMeasurements,
        netContent: netContentObj,
      },
    }

    temp.variableTradeItemInformationModule = {
      ...temp.variableTradeItemInformationModule,
      variableTradeItemInformation: {
        ...temp.variableTradeItemInformationModule
          ?.variableTradeItemInformation,
        variableTradeItemTypeCode: values.variableTradeItemTypeCode
          ? values.variableTradeItemTypeCode
          : null,
      },
    }

    await mutateAsync(masterProductCopy, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  if (isLoadingProduct || isLoading) return <LoadingIndicator />
  if (
    error ||
    measurementUnitCodeError ||
    pricedByMeasureTypeCodeError ||
    sellingByMeasureTypeCodeError ||
    variableTradeItemTypeCodeError ||
    salesConditionCodeError
  )
    return <h1>Error</h1>
  return (
    <Box maxWidth='500px'>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => handleSubmit(values)}>
        {({ dirty, setFieldValue, values, errors, isValid }) => (
          <Form>
            <Stack mt={5} spacing={3} mb={5}>
              <AutocompleteWithCodeList
                fieldName='displayUnit'
                codes={
                  displayUnits?.map((code: { code: string }) => code.code) || []
                }
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: theme.spacing(2),
                }}>
                <Input
                  errorMessage={errors.netContent as string | undefined}
                  label={t('netContent')}
                  name='netContent'
                  type='text'
                  variant='filled'
                  flexBasis='50%'
                  marginBottom='0px'
                />
                <Box sx={{ flexBasis: '50%' }}>
                  <Autocomplete
                    disablePortal
                    noOptionsText={
                      measurementUnitCodes.length === 0
                        ? t('noOptions')
                        : t('noOptionsAutoComplete')
                    }
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.value}>
                          {option.label}
                        </li>
                      )
                    }}
                    options={measurementUnitCodes ?? []}
                    onChange={(e, value, reason, details) => {
                      if (reason === 'selectOption') {
                        if (details) {
                          setFieldValue('netContentUnit', details.option)
                        }
                      }
                      if (reason === 'clear') {
                        setFieldValue('netContentUnit', null)
                      }
                    }}
                    value={
                      (values as FormikFieldType)[
                        'netContentUnit' as keyof FormikFieldType
                      ] as { label: string; value: string }
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    getOptionLabel={(option) => option.label ?? ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name='netContentUnit'
                        label={t('netContentUnit')}
                        InputLabelProps={{
                          shrink: true,
                          sx: { top: '-5px', left: '-4px' },
                        }}
                        variant='filled'
                        sx={{
                          '& .MuiFilledInput-root': {
                            paddingTop: theme.spacing(0.5),
                          },
                        }}
                      />
                    )}
                    id='netContentUnit'
                  />
                </Box>
              </Box>
              <AutocompleteWithCodeList
                fieldName='pricedBy'
                codes={
                  pricedByMeasureTypeCodes?.map(
                    (code: { code: string }) => code.code
                  ) || []
                }
              />
              <AutocompleteWithCodeList
                fieldName='pickedBy'
                codes={
                  sellingByMeasureTypeCodes?.map(
                    (code: { code: string }) => code.code
                  ) || []
                }
              />
              <AutocompleteWithCodeList
                fieldName='variableTradeItemTypeCode'
                codes={
                  variableTradeItemTypeCodes?.map(
                    (code: { code: string }) => code.code
                  ) || []
                }
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: theme.spacing(2),
                }}>
                <Input
                  errorMessage={errors.initialValue as string | undefined}
                  label={t('initialValueWhenAddedToCart')}
                  name='initialValue'
                  type='text'
                  variant='filled'
                  flexBasis='50%'
                  marginBottom='0px'
                />
                <Input
                  errorMessage={errors.incrementValue as string | undefined}
                  label={t('incrementValue')}
                  name='incrementValue'
                  type='text'
                  variant='filled'
                  flexBasis='50%'
                  marginBottom='0px'
                />
              </Box>
            </Stack>
            <Typography variant='body2'>{t('salesConditionCode')}</Typography>
            <Box pl={1} mb={8} mt={1}>
              {salesConditionCodes.map(
                (salesCondition: { label: string; value: string }) => {
                  return (
                    <CheckboxLabel
                      key={salesCondition.value}
                      value={Boolean(
                        values.salesConditions?.length > 0 &&
                          values.salesConditions.includes(salesCondition.value)
                      )}
                      label={salesCondition.label || ''}
                      handleChange={() =>
                        handleCheckboxChange(
                          values.salesConditions,
                          setFieldValue,
                          salesCondition
                        )
                      }
                    />
                  )
                }
              )}
            </Box>
            <Box
              mt={3}
              sx={{
                position: 'fixed',
                bottom: isMobile ? '0px' : theme.spacing(2),
                width: isMobile ? '92%' : '504px',
                zIndex: 1000,
              }}>
              <SaveBtn
                isValid={isValid}
                dirty={dirty}
                showOverlay
                isLoadingRequest={isLoadingPutRequest}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
